import { useState } from 'react';
import {
    Layout,
    Button,
    Input,
    Card,
    Typography,
    Space,
    Row,
    Col,
    Menu,
    message,
    Grid,
    Tag,
    Drawer,
    Modal,
    Form
} from 'antd';
import {
    BarChartOutlined,
    SearchOutlined,
    MenuOutlined,
    CloseOutlined,
    GithubOutlined,
    FileTextOutlined,
    MailOutlined,
    TeamOutlined,
    InfoCircleOutlined,
} from '@ant-design/icons';
import ragomicsLogo from '../assets/logo.png';
import workflowDiagram from '../assets/workflow-diagram.png';
import AboutModal from './AboutPage';
import TeamModal from './TeamModal';
import AnalysisGallery from './AnalysisGallery';
const { Header, Content, Footer } = Layout;
const { Title, Paragraph, Text } = Typography;
const { useBreakpoint } = Grid;

const RagomicsWebsite = () => {
    const [email, setEmail] = useState('');
    const [mobileMenuVisible, setMobileMenuVisible] = useState(false);
    const screens = useBreakpoint();
    const [aboutModalVisible, setAboutModalVisible] = useState(false);
    const [teamModalVisible, setTeamModalVisible] = useState(false);
    const [demoModalVisible, setDemoModalVisible] = useState(false);
    const [demoForm] = Form.useForm();

    const showAboutModal = () => {
        setAboutModalVisible(true);
        setMobileMenuVisible(false); // Close mobile menu when opening modal
    };

    const handleAboutModalCancel = () => {
        setAboutModalVisible(false);
    };

    const showTeamModal = () => {
        setTeamModalVisible(true);
        setMobileMenuVisible(false);
    };

    const handleTeamModalCancel = () => {
        setTeamModalVisible(false);
    };

    const showDemoModal = () => {
        setDemoModalVisible(true);
    };

    const handleDemoModalCancel = () => {
        setDemoModalVisible(false);
        demoForm.resetFields();
    };

    const handleDemoSubmit = async () => {
        try {
            const values = await demoForm.validateFields();
            const subject = encodeURIComponent('Demo Request from ' + values.name);
            const body = encodeURIComponent(
                `Name: ${values.name}\nOrganization: ${values.organization || 'N/A'}\nEmail: ${values.email}\n\nMessage:\n${values.message || 'I would like to request a demo of the Ragomics platform.'}`
            );
            window.open(`mailto:ragene.genomics@gmail.com?subject=${subject}&body=${body}`, '_blank');
            message.success('Opening your email client...');
            setDemoModalVisible(false);
            demoForm.resetFields();
        } catch {
            // validation failed
        }
    };

    const showMobileMenu = () => {
        setMobileMenuVisible(true);
    };

    const hideMobileMenu = () => {
        setMobileMenuVisible(false);
    };

    const handleSubscribe = () => {
        if (email) {
            message.success('Thank you for subscribing!');
            setEmail('');
        } else {
            message.error('Please enter your email address');
        }
    };

    const sidebarItems = [
        {
            key: 'overview',
            icon: <BarChartOutlined />,
            label: 'Gallery Overview',
        },
    ];

    const platformStats = [
        {
            category: "Function Blocks",
            count: 200,
            description: "Pre-built function blocks for common tasks",
            examples: ["Preprocess", "scAR", "Recipe Monocle", "Reduce Dimension"]
        },
        {
            category: "Reference Databases",
            count: 3100,
            description: "Integrated biological and genomic databases",
            examples: ["Ensembl", "NCBI", "UniProt", "GO"]
        },
        {
            category: "Multi-Omics",
            count: 4,
            description: "Support for multi-omics data types",
            examples: ["scRNA-seq", "scATAC-seq", "Spatial Transcriptomics", "Proteomics"]
        }
    ];

    const mobileNavItems = [
        {
            key: 'about',
            icon: <InfoCircleOutlined />,
            label: 'About',
            onClick: showAboutModal
        },
        {
            key: 'team',
            icon: <TeamOutlined />,
            label: 'Team',
            onClick: showTeamModal
        },
        {
            key: 'github',
            icon: <GithubOutlined />,
            label: 'Github',
            onClick: () => {
                setMobileMenuVisible(false);
            }
        },
        {
            key: 'paper',
            icon: <FileTextOutlined />,
            label: 'Paper',
            onClick: () => {
                setMobileMenuVisible(false);
            }
        },
        {
            key: 'contact',
            icon: <MailOutlined />,
            label: 'Contact',
            onClick: () => {
                setMobileMenuVisible(false);
                window.location.href = 'mailto:Ragomics.genomics@gmail.com';
            }
        }
    ];

    return (
        <Layout style={{ minHeight: '100vh' }}>
            {/* Header */}
            <Header style={{
                background: '#fff',
                padding: '0 16px',
                borderBottom: '1px solid #e8e8e8',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
                position: 'sticky',
                top: 0,
                zIndex: 1000,
                height: 'auto',
                minHeight: 64
            }}>
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    maxWidth: 1200,
                    margin: '0 auto',
                    height: '100%',
                    minHeight: 64
                }}>
                    {/* Logo Section */}
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        minWidth: screens.xs ? 120 : 200,
                        flex: '0 0 auto'
                    }}>
                        <img
                            src={ragomicsLogo}
                            alt="Ragomics Logo"
                            style={{
                                height: screens.xs ? 32 : 44,
                                width: 'auto',
                                objectFit: 'contain',
                                borderRadius: 8,
                                transition: 'transform 0.2s ease',
                                cursor: 'pointer'
                            }}
                        />
                    </div>

                    {/* Center Navigation - Only show on medium screens and up */}
                    {screens.md && (
                        <div style={{
                            flex: '1 1 auto',
                            display: 'flex',
                            justifyContent: 'center'
                        }}>
                            <div style={{
                                display: 'flex',
                                gap: 8,
                                padding: '6px',
                                borderRadius: 12,
                            }}>
                                <button
                                    onClick={showAboutModal}
                                    style={{
                                        padding: '10px 20px',
                                        borderRadius: 8,
                                        border: 'none',
                                        background: '#fff',
                                        color: '#2c3e50',
                                        fontSize: 14,
                                        fontWeight: 500,
                                        cursor: 'pointer',
                                        transition: 'all 0.2s ease',
                                        boxShadow: '0 2px 4px rgba(0,0,0,0.08)'
                                    }}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.transform = 'translateY(-1px)';
                                        e.currentTarget.style.boxShadow = '0 4px 8px rgba(114, 120, 185, 0.3)';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.transform = 'translateY(0)';
                                        e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.08)';
                                    }}
                                >
                                    About
                                </button>
                                <button
                                    onClick={showTeamModal}
                                    style={{
                                        padding: '10px 20px',
                                        borderRadius: 8,
                                        border: 'none',
                                        background: '#fff',
                                        color: '#2c3e50',
                                        fontSize: 14,
                                        fontWeight: 500,
                                        cursor: 'pointer',
                                        transition: 'all 0.2s ease',
                                        boxShadow: '0 2px 4px rgba(0,0,0,0.08)'
                                    }}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.transform = 'translateY(-1px)';
                                        e.currentTarget.style.boxShadow = '0 4px 8px rgba(114, 120, 185, 0.3)';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.transform = 'translateY(0)';
                                        e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.08)';
                                    }}
                                >
                                    Team
                                </button>
                                <button
                                    style={{
                                        padding: '10px 20px',
                                        borderRadius: 8,
                                        border: 'none',
                                        background: '#fff',
                                        color: '#2c3e50',
                                        fontSize: 14,
                                        fontWeight: 500,
                                        cursor: 'pointer',
                                        transition: 'all 0.2s ease',
                                        boxShadow: '0 2px 4px rgba(0,0,0,0.08)'
                                    }}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.transform = 'translateY(-1px)';
                                        e.currentTarget.style.boxShadow = '0 4px 8px rgba(114, 120, 185, 0.3)';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.transform = 'translateY(0)';
                                        e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.08)';
                                    }}
                                >
                                    Github
                                </button>
                                <button
                                    style={{
                                        padding: '10px 20px',
                                        borderRadius: 8,
                                        border: 'none',
                                        background: '#fff',
                                        color: '#2c3e50',
                                        fontSize: 14,
                                        fontWeight: 500,
                                        cursor: 'pointer',
                                        transition: 'all 0.2s ease',
                                        boxShadow: '0 2px 4px rgba(0,0,0,0.08)'
                                    }}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.transform = 'translateY(-1px)';
                                        e.currentTarget.style.boxShadow = '0 4px 8px rgba(114, 120, 185, 0.3)';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.transform = 'translateY(0)';
                                        e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.08)';
                                    }}
                                >
                                    Paper
                                </button>
                                <button
                                    onClick={() => window.location.href = 'mailto:ragene.genomics@gmail.com'}
                                    style={{
                                        padding: '10px 20px',
                                        borderRadius: 8,
                                        border: 'none',
                                        background: '#fff',
                                        color: '#2c3e50',
                                        fontSize: 14,
                                        fontWeight: 500,
                                        cursor: 'pointer',
                                        transition: 'all 0.2s ease',
                                        boxShadow: '0 2px 4px rgba(0,0,0,0.08)'
                                    }}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.transform = 'translateY(-1px)';
                                        e.currentTarget.style.boxShadow = '0 4px 8px rgba(114, 120, 185, 0.3)';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.transform = 'translateY(0)';
                                        e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.08)';
                                    }}
                                >
                                    Contact
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Right Section - Actions */}
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 12,
                        minWidth: screens.xs ? 60 : 200,
                        justifyContent: 'flex-end',
                        flex: '0 0 auto'
                    }}>
                        {/* Mobile Menu Button - Only show on small screens */}
                        {!screens.md && (
                            <Button
                                type="text"
                                icon={<MenuOutlined />}
                                onClick={showMobileMenu}
                                style={{
                                    border: 'none',
                                    boxShadow: 'none',
                                    color: '#4a5568',
                                    fontSize: 18,
                                    width: 40,
                                    height: 40,
                                    borderRadius: 8,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}
                            />
                        )}
                    </div>
                </div>
            </Header>

            {/* Mobile Menu Drawer */}
            <Drawer
                title={
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <img
                            src={ragomicsLogo}
                            alt="Ragomics Logo"
                            style={{ height: 24, width: 'auto' }}
                        />
                        <span style={{ fontSize: 16, fontWeight: 600 }}>Menu</span>
                    </div>
                }
                placement="right"
                onClose={hideMobileMenu}
                open={mobileMenuVisible}
                width={280}
                closeIcon={<CloseOutlined />}
                styles={{
                    body: { padding: 0 }
                }}
            >
                <Menu
                    mode="vertical"
                    style={{ border: 'none', height: '100%' }}
                    items={mobileNavItems.map(item => ({
                        ...item,
                        style: {
                            height: 56,
                            display: 'flex',
                            alignItems: 'center',
                            fontSize: 16,
                            fontWeight: 500
                        }
                    }))}
                    onClick={({ key }) => {
                        const item = mobileNavItems.find(nav => nav.key === key);
                        if (item?.onClick) {
                            item.onClick();
                        }
                    }}
                />
            </Drawer>

            <Content>
                {/* Hero Section */}
                <div style={{
                    background: '#fff',
                    padding: screens.xs ? '60px 16px 12px 16px' : '80px 24px 12px 24px',
                }}>
                    <div style={{ maxWidth: 900, margin: '0 auto', textAlign: 'center' }}>
                        <Title level={1} style={{
                            fontSize: screens.xs ? 20 : screens.md ? 34 : 26,
                            marginBottom: 16,
                            marginTop: 0,
                            color: '#1a365d',
                            fontWeight: 600,
                            letterSpacing: '-0.5px',
                            lineHeight: 1.3
                        }}>
                            Multi-Agent Computational Platform for Single-Cell Genomics Research
                        </Title>
                        <Text style={{
                            fontSize: screens.xs ? 12 : 14,
                            color: '#718096',
                            textTransform: 'uppercase',
                            letterSpacing: '1px',
                            fontWeight: 500,
                            display: 'block',
                            marginBottom: 32
                        }}>
                            Research Computing & Bioinformatics Laboratory
                        </Text>

                        <div style={{
                            display: 'flex',
                            justifyContent: 'center',
                            marginBottom: 32,
                            padding: screens.xs ? '12px' : '16px',
                            background: '#fff',
                            borderRadius: 8,
                            boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
                            border: '1px solid #f0f0f0'
                        }}>
                            <img
                                src={workflowDiagram}
                                alt="Ragomics Workflow Architecture Diagram"
                                style={{
                                    maxWidth: screens.xs ? '100%' : '80%',
                                    height: 'auto',
                                    borderRadius: 6,
                                    display: 'block'
                                }}
                            />
                        </div>

                        <Paragraph style={{
                            fontSize: screens.xs ? 16 : 18,
                            color: '#4a5568',
                            marginBottom: 40,
                            maxWidth: 720,
                            margin: '0 auto 40px',
                            lineHeight: 1.7,
                            textAlign: screens.xs ? 'left' : 'justify'
                        }}>
                            Empowering researchers with intelligent, tree-based workflows for single-cell RNA sequencing, multi-omics integration, spatial transcriptomics, and drug discovery. Reproducible. Accessible. Agent-assisted.
                        </Paragraph>

                        <div style={{
                            display: 'flex',
                            justifyContent: 'center',
                            gap: screens.xs ? 8 : 16,
                            flexWrap: 'wrap',
                            marginBottom: screens.xs ? 32 : 48
                        }}>
                            <button
                                onClick={showDemoModal}
                                style={{
                                    background: '#fff',
                                    border: '2px solid #4a5568',
                                    borderRadius: 4,
                                    color: '#4a5568',
                                    fontSize: screens.xs ? 12 : 14,
                                    fontWeight: 500,
                                    padding: screens.xs ? '10px 16px' : '12px 24px',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s ease',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.5px'
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.background = '#4a5568';
                                    e.currentTarget.style.color = '#fff';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.background = '#fff';
                                    e.currentTarget.style.color = '#4a5568';
                                }}
                            >
                                Request Demo
                            </button>
                            <button
                                onClick={() => window.open('https://ragomics-doc.readthedocs.io/en/latest/index.html', '_blank')}
                                style={{
                                    background: 'transparent',
                                    border: '2px solid #e2e8f0',
                                    borderRadius: 4,
                                    color: '#718096',
                                    fontSize: screens.xs ? 12 : 14,
                                    fontWeight: 500,
                                    padding: screens.xs ? '10px 16px' : '12px 24px',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s ease',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.5px'
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.borderColor = '#cbd5e0';
                                    e.currentTarget.style.color = '#4a5568';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.borderColor = '#e2e8f0';
                                    e.currentTarget.style.color = '#718096';
                                }}
                            >
                                Documentation
                            </button>
                        </div>
                    </div>
                </div>

                {/* Dashboard Preview */}
                <div style={{ padding: screens.xs ? '32px 16px' : '40px 24px', background: '#fff' }}>
                    <div style={{ maxWidth: 1152, margin: '0 auto' }}>
                        <Card
                            style={{
                                borderRadius: 16,
                                boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
                                border: '1px solid #f0f0f0'
                            }}
                        >
                            {/* Dashboard Header */}
                            <div style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                marginBottom: 32,
                                flexDirection: screens.xs ? 'column' : 'row',
                                gap: screens.xs ? 16 : 0
                            }}>
                                <Space size="large">
                                    <Input
                                        placeholder="Search"
                                        prefix={<SearchOutlined />}
                                        style={{ width: screens.xs ? 250 : 256 }}
                                    />
                                </Space>
                            </div>

                            {/* Dashboard Content */}
                            <Row gutter={screens.xs ? [16, 16] : 32}>
                                {/* Sidebar */}
                                <Col xs={24} lg={6}>
                                    <Menu
                                        mode="inline"
                                        defaultSelectedKeys={['overview']}
                                        style={{ border: 'none' }}
                                        items={sidebarItems}
                                    />
                                </Col>

                                {/* Main Content */}
                                <Col xs={24} lg={18}>
                                    <AnalysisGallery />
                                </Col>
                            </Row>
                        </Card>
                    </div>
                </div>

                {/* Research Overview Section */}
                <div style={{ padding: screens.xs ? '40px 16px' : '60px 24px', background: '#fff' }}>
                    <div style={{ maxWidth: 1000, margin: '0 auto' }}>
                        <div style={{ textAlign: 'center', marginBottom: 48 }}>
                            <Title level={2} style={{
                                color: '#1a365d',
                                fontSize: screens.xs ? 22 : 28,
                                fontWeight: 600,
                                marginBottom: 16
                            }}>
                                Research Focus
                            </Title>
                            <Paragraph style={{
                                fontSize: screens.xs ? 14 : 16,
                                color: '#4a5568',
                                maxWidth: 600,
                                margin: '0 auto',
                                lineHeight: 1.6,
                                padding: screens.xs ? '0 8px' : 0
                            }}>
                                Our computational biology group focuses on developing methods and tools
                                that advance single-cell genomics research across multiple biological domains.
                            </Paragraph>
                        </div>

                        <Row gutter={[24, 24]}>
                            {platformStats.map((stat, index) => (
                                <Col xs={24} md={8} key={index}>
                                    <Card style={{
                                        height: '100%',
                                        border: '1px solid #e2e8f0',
                                        borderRadius: 6,
                                        background: '#fff',
                                        transition: 'all 0.2s ease',
                                        cursor: 'default',
                                    }}
                                        hoverable
                                        onMouseEnter={(e) => {
                                            e.currentTarget.style.borderColor = '#cbd5e0';
                                            e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.08)';
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.borderColor = '#e2e8f0';
                                            e.currentTarget.style.boxShadow = 'none';
                                        }}
                                        styles={{ body: { padding: screens.xs ? '20px' : '24px', textAlign: 'center' } }}
                                    >
                                        <div style={{ marginBottom: 16 }}>
                                            <Text style={{
                                                fontSize: screens.xs ? 28 : 32,
                                                fontWeight: 600,
                                                color: '#1a365d',
                                                display: 'block'
                                            }}>
                                                {stat.count}+
                                            </Text>
                                            <Title level={4} style={{
                                                margin: '8px 0 12px 0',
                                                fontSize: screens.xs ? 14 : 16,
                                                color: '#2d3748',
                                                fontWeight: 600
                                            }}>
                                                {stat.category}
                                            </Title>
                                        </div>
                                        <Paragraph style={{
                                            fontSize: screens.xs ? 13 : 14,
                                            color: '#4a5568',
                                            marginBottom: 16,
                                            lineHeight: 1.5
                                        }}>
                                            {stat.description}
                                        </Paragraph>
                                        <div>
                                            <Text style={{
                                                fontSize: 12,
                                                color: '#718096',
                                                fontWeight: 500,
                                                textTransform: 'uppercase',
                                                letterSpacing: '0.5px'
                                            }}>
                                                Examples
                                            </Text>
                                            <div style={{ marginTop: 8 }}>
                                                {stat.examples.map((example, idx) => (
                                                    <Tag
                                                        key={idx}
                                                        style={{
                                                            fontSize: 11,
                                                            margin: '2px 3px',
                                                            background: '#edf2f7',
                                                            border: '1px solid #e2e8f0',
                                                            color: '#4a5568',
                                                            borderRadius: 4
                                                        }}
                                                    >
                                                        {example}
                                                    </Tag>
                                                ))}
                                            </div>
                                        </div>
                                    </Card>
                                </Col>
                            ))}
                        </Row>
                    </div>
                </div>

                {/* Institutional Affiliations */}
                {/* <div style={{
                    padding: '60px 24px',
                    background: '#fff'
                }}>
                    <div style={{ maxWidth: 1000, margin: '0 auto' }}>
                        <div style={{ textAlign: 'center', marginBottom: 48 }}>
                            <Title level={3} style={{
                                color: '#1a365d',
                                fontSize: 20,
                                fontWeight: 600,
                                marginBottom: 8
                            }}>
                                Institutional Collaborations
                            </Title>
                            <Text style={{
                                fontSize: 14,
                                color: '#718096',
                                textTransform: 'uppercase',
                                letterSpacing: '1px'
                            }}>
                                Academic Research Partners
                            </Text>
                        </div>

                        <Row
                            gutter={[32, 32]}
                            justify="center"
                            align="middle"
                            style={{ maxWidth: 800, margin: '0 auto' }}
                        >
                            {institutionLogos.map((institution, index) => (
                                <Col
                                    key={index}
                                    xs={24}
                                    sm={12}
                                    md={8}
                                    style={{
                                        display: 'flex',
                                        justifyContent: 'center',
                                        alignItems: 'center'
                                    }}
                                >
                                    <div style={{
                                        padding: '16px',
                                        borderRadius: '6px',
                                        transition: 'all 0.2s ease',
                                        cursor: 'default',
                                        border: '1px solid transparent'
                                    }}
                                        onMouseEnter={(e) => {
                                            e.currentTarget.style.borderColor = '#e2e8f0';
                                            e.currentTarget.style.background = '#f7fafc';
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.borderColor = 'transparent';
                                            e.currentTarget.style.background = 'transparent';
                                        }}
                                    >
                                        <img
                                            src={institution.logo}
                                            alt={institution.alt}
                                            style={{
                                                height: screens.md ? 80 : 60,
                                                width: 'auto',
                                                maxWidth: '180px',
                                                objectFit: 'contain',
                                                filter: 'grayscale(20%)',
                                                opacity: 0.8
                                            }}
                                        />
                                    </div>
                                </Col>
                            ))}
                        </Row>
                    </div>
                </div> */}

                {/* Updates Section */}
                <div style={{ padding: screens.xs ? '40px 16px' : '60px 24px', background: '#fff', borderTop: '1px solid #e2e8f0' }}>
                    <div style={{ maxWidth: 600, margin: '0 auto', textAlign: 'center' }}>
                        <Title level={3} style={{
                            color: '#1a365d',
                            fontSize: screens.xs ? 20 : 24,
                            fontWeight: 600,
                            marginBottom: 16
                        }}>
                            Research Updates & Announcements
                        </Title>
                        <Paragraph style={{
                            color: '#4a5568',
                            marginBottom: 32,
                            fontSize: screens.xs ? 14 : 16,
                            lineHeight: 1.6,
                            padding: screens.xs ? '0 8px' : 0
                        }}>
                            Subscribe to receive notifications about new computational tools,
                            research publications, and platform updates from our laboratory.
                        </Paragraph>

                        <div style={{
                            display: 'flex',
                            flexDirection: screens.sm ? 'row' : 'column',
                            justifyContent: 'center',
                            gap: 12,
                            marginBottom: 16
                        }}>
                            <Input
                                placeholder="Enter your institutional email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                style={{
                                    width: screens.sm ? 320 : '100%',
                                    borderColor: '#e2e8f0',
                                    borderRadius: 4
                                }}
                                size="large"
                            />
                            <button
                                onClick={handleSubscribe}
                                style={{
                                    background: '#fff',
                                    border: '2px solid #4a5568',
                                    borderRadius: 4,
                                    color: '#4a5568',
                                    fontSize: screens.xs ? 12 : 14,
                                    fontWeight: 500,
                                    padding: screens.xs ? '10px 16px' : '12px 20px',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s ease',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.5px',
                                    whiteSpace: 'nowrap'
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.background = '#4a5568';
                                    e.currentTarget.style.color = '#fff';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.background = '#fff';
                                    e.currentTarget.style.color = '#4a5568';
                                }}
                            >
                                Subscribe
                            </button>
                        </div>

                        <Text style={{
                            fontSize: 12,
                            color: '#718096',
                            fontStyle: 'italic'
                        }}>
                            We respect your privacy and follow institutional data protection guidelines.
                        </Text>
                    </div>
                </div>
            </Content>

            {/* Footer */}
            <Footer style={{ background: '#fff', padding: screens.xs ? '24px 16px' : '24px' }}>
                <div style={{ maxWidth: 1152, margin: '0 auto', textAlign: 'center' }}>
                    <Text type="secondary" style={{ fontSize: screens.xs ? 12 : 14 }}>
                        © 2025 RagomicsBioInc. All rights reserved.
                    </Text>
                    <div style={{ marginTop: 8 }}>
                        <Text type="secondary" style={{ fontSize: screens.xs ? 8 : 10 }}>
                            <a style={{ color: 'inherit', textDecoration: 'none' }} href="https://beian.miit.gov.cn/">
                                备案号：沪ICP备2021009218号-1
                            </a>
                        </Text>
                    </div>
                </div>
            </Footer>

            {/* About Modal */}
            <AboutModal
                visible={aboutModalVisible}
                onCancel={handleAboutModalCancel}
            />

            {/* Team Modal */}
            <TeamModal
                visible={teamModalVisible}
                onCancel={handleTeamModalCancel}
            />

            {/* Demo Request Modal */}
            <Modal
                open={demoModalVisible}
                onCancel={handleDemoModalCancel}
                title={null}
                footer={null}
                width={480}
                centered
            >
                <div style={{ textAlign: 'center', marginBottom: 24 }}>
                    <MailOutlined style={{ fontSize: 36, color: '#4a5568', marginBottom: 12 }} />
                    <Title level={4} style={{ margin: 0, color: '#2d3748' }}>Request a Demo</Title>
                    <Text type="secondary">
                        Fill out the form below and we'll get in touch with you.
                    </Text>
                </div>
                <Form form={demoForm} layout="vertical">
                    <Form.Item
                        name="name"
                        label="Name"
                        rules={[{ required: true, message: 'Please enter your name' }]}
                    >
                        <Input placeholder="Your name" />
                    </Form.Item>
                    <Form.Item
                        name="email"
                        label="Email"
                        rules={[
                            { required: true, message: 'Please enter your email' },
                            { type: 'email', message: 'Please enter a valid email' }
                        ]}
                    >
                        <Input placeholder="your@email.com" />
                    </Form.Item>
                    <Form.Item name="organization" label="Organization">
                        <Input placeholder="Your organization (optional)" />
                    </Form.Item>
                    <Form.Item name="message" label="Message">
                        <Input.TextArea rows={3} placeholder="Tell us about your research needs (optional)" />
                    </Form.Item>
                    <Form.Item style={{ marginBottom: 0 }}>
                        <Button
                            type="primary"
                            block
                            onClick={handleDemoSubmit}
                            style={{ background: '#4a5568', borderColor: '#4a5568', height: 40 }}
                        >
                            Send Request
                        </Button>
                        <div style={{ textAlign: 'center', marginTop: 12 }}>
                            <Text type="secondary" style={{ fontSize: 12 }}>
                                Or email us directly at{' '}
                                <a href="mailto:ragene.genomics@gmail.com">ragene.genomics@gmail.com</a>
                            </Text>
                        </div>
                    </Form.Item>
                </Form>
            </Modal>
        </Layout>
    );
};

export default RagomicsWebsite;